import React, { useState } from 'react';
import { Form, Input, Button, Card, message, Tabs, Tag, Divider } from 'antd';
import { UserOutlined, LockOutlined, SafetyOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useAuthStore } from '../store/authStore';
import { login, getRoleLabel, getRoleColor } from '../api/authApi';
import { Role } from '../types';

interface LoginPageProps {
  onLogin: () => void;
}

const DEMO_ACCOUNTS = [
  { username: 'superadmin', password: 'admin123', role: 'super_admin' as Role, desc: '全功能访问' },
  { username: 'admin',      password: 'admin123', role: 'admin'       as Role, desc: '平台管理' },
  { username: 'agent_sz',   password: '123456',   role: 'agent'       as Role, desc: '代理商视图' },
  { username: 'client_gz',  password: '123456',   role: 'client'      as Role, desc: '终端客户视图' },
];

const LoginPage: React.FC<LoginPageProps> = ({ onLogin }) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const { login: storeLogin } = useAuthStore();

  const handleLogin = async (values: { username: string; password: string }) => {
    setLoading(true);
    try {
      const user = await login(values.username, values.password);
      storeLogin(user);
      message.success(`欢迎回来，${user.name}！`);
      onLogin();
    } catch (e: any) {
      message.error(e.message || '登录失败');
    } finally {
      setLoading(false);
    }
  };

  const quickLogin = (username: string, password: string) => {
    form.setFieldsValue({ username, password });
    handleLogin({ username, password });
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a1628 0%, #0d2240 40%, #0a3d62 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* 背景装饰 */}
      {[...Array(6)].map((_, i) => (
        <div key={i} style={{
          position: 'absolute',
          borderRadius: '50%',
          border: '1px solid rgba(24,144,255,0.15)',
          width: 200 + i * 120,
          height: 200 + i * 120,
          top: '50%', left: '50%',
          transform: 'translate(-50%,-50%)',
          animation: `pulse ${3 + i}s ease-in-out infinite alternate`,
        }} />
      ))}

      <div style={{ width: 420, position: 'relative', zIndex: 1 }}>
        {/* Logo区 */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            width: 64, height: 64, borderRadius: 16,
            background: 'linear-gradient(135deg, #1890ff, #096dd9)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 12, boxShadow: '0 8px 24px rgba(24,144,255,0.4)',
          }}>
            <SafetyOutlined style={{ fontSize: 32, color: '#fff' }} />
          </div>
          <h1 style={{ color: '#fff', fontSize: 24, margin: 0, fontWeight: 700 }}>
            物联网卡管理平台
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, margin: '6px 0 0' }}>
            IoT Card Management Platform (CMP)
          </p>
        </div>

        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            background: 'rgba(255,255,255,0.08)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.12)',
            boxShadow: '0 24px 48px rgba(0,0,0,0.4)',
          }}
          styles={{ body: { padding: 32 } }}
        >
          <Form form={form} onFinish={handleLogin} size="large" autoComplete="off">
            <Form.Item name="username" rules={[{ required: true, message: '请输入用户名' }]}>
              <Input
                prefix={<UserOutlined style={{ color: 'rgba(255,255,255,0.4)' }} />}
                placeholder="用户名"
                style={{
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  color: '#fff',
                  borderRadius: 8,
                }}
              />
            </Form.Item>
            <Form.Item name="password" rules={[{ required: true, message: '请输入密码' }]}>
              <Input.Password
                prefix={<LockOutlined style={{ color: 'rgba(255,255,255,0.4)' }} />}
                placeholder="密码"
                style={{
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  color: '#fff',
                  borderRadius: 8,
                }}
              />
            </Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={loading}
              icon={<ThunderboltOutlined />}
              style={{
                height: 44, borderRadius: 8, fontSize: 15, fontWeight: 600,
                background: 'linear-gradient(90deg, #1890ff, #096dd9)',
                border: 'none',
                boxShadow: '0 4px 16px rgba(24,144,255,0.4)',
              }}
            >
              登录
            </Button>
          </Form>

          <Divider style={{ borderColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.3)', fontSize: 12 }}>
            演示账号快速登录
          </Divider>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {DEMO_ACCOUNTS.map(acc => (
              <div
                key={acc.username}
                onClick={() => quickLogin(acc.username, acc.password)}
                style={{
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.1)',
                  background: 'rgba(255,255,255,0.05)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(24,144,255,0.15)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.05)')}
              >
                <Tag color={getRoleColor(acc.role)} style={{ fontSize: 11, marginBottom: 4 }}>
                  {getRoleLabel(acc.role)}
                </Tag>
                <div style={{ color: '#fff', fontSize: 12, fontWeight: 500 }}>{acc.username}</div>
                <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>{acc.desc}</div>
              </div>
            ))}
          </div>
        </Card>

        <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.25)', fontSize: 12, marginTop: 20 }}>
          © 2026 CMP Platform · 私有化部署版本
        </p>
      </div>

      <style>{`
        @keyframes pulse { from { opacity: 0.3; transform: translate(-50%,-50%) scale(0.98); } to { opacity: 0.6; transform: translate(-50%,-50%) scale(1.02); } }
        .ant-input { color: #fff !important; }
        .ant-input::placeholder { color: rgba(255,255,255,0.3) !important; }
        .ant-input-password input { color: #fff !important; background: transparent !important; }
        .ant-input-affix-wrapper { background: rgba(255,255,255,0.08) !important; border: 1px solid rgba(255,255,255,0.15) !important; border-radius: 8px !important; }
        .ant-input-affix-wrapper:hover, .ant-input-affix-wrapper-focused { border-color: #1890ff !important; }
      `}</style>
    </div>
  );
};

export default LoginPage;
